import { useEffect, useState } from "react";
import { Col, FormGroup, FormLabel, Row, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import InputText from "../../../../components/Forms/Element/InputText";
import TextArea from "../../../../components/Forms/Element/TextArea";
import StatusCheckBox from "../../../../components/Common/StatusCheckbox";
import AsteriskRequired from "../../../../components/Common/AsteriskRequired";
import CustomSelect from "../../../../components/Forms/Element/CustomSelect";
import HintTooltip from "../../../../components/Common/HintTooltip";
import { convertStatusToBoolean } from "../../../../../utils/ConverterUtils";
import FormSection from "../../../../components/Common/Form/FormSection";
import MultiSelect from "../../../../components/Forms/Element/MultiSelect";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { SearchOperation } from "../../../../../utils/ApiUtils";
import "./WhatsappBotForm.css";

const Form = (props) => {
    const { t } = useTranslation();
    const [serviceOptions, setServiceOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [planOptions, setPlanOptions] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [servicesLoading, setServicesLoading] = useState(true);
    const [productsLoading, setProductsLoading] = useState(true);
    const [plansLoading, setPlansLoading] = useState(true);
    const [servicesLoadFailed, setServicesLoadFailed] = useState(false);
    const [productsLoadFailed, setProductsLoadFailed] = useState(false);
    const [plansLoadFailed, setPlansLoadFailed] = useState(false);
    const [branchesLoading, setBranchesLoading] = useState(true);
    const [branchesLoadFailed, setBranchesLoadFailed] = useState(false);
    const [employeesLoading, setEmployeesLoading] = useState(true);
    const [employeesLoadFailed, setEmployeesLoadFailed] = useState(false);
    const {
        status,
        setStatus,
        formik,
        entityData
    } = props;

    const communicationStyleOptions = [
        { value: 'FRIENDLY', label: t('chatbot_style_friendly') },
        { value: 'PROFESSIONAL', label: t('chatbot_style_professional') },
        { value: 'DIRECT', label: t('chatbot_style_direct') },
        { value: 'WELCOMING', label: t('chatbot_style_welcoming') }
    ];

    useEffect(() => {
        const loadCatalogOptions = (type, setOptions, setLoadFailed, setLoading) => {
            axiosInstance.post(`${Api.main.product}/findByFilters`, {
                filters: [
                    { field: "type", operation: SearchOperation.EQUAL, value: type },
                    { field: "status", operation: SearchOperation.EQUAL, value: "ACTIVE" }
                ]
            }).then(({ data }) => {
                setOptions((data || []).map(item => ({ value: item.id, label: item.name })));
            }).catch(() => {
                setOptions([]);
                setLoadFailed(true);
            }).finally(() => setLoading(false));
        };

        loadCatalogOptions("SERVICE", setServiceOptions, setServicesLoadFailed, setServicesLoading);
        loadCatalogOptions("PRODUCT", setProductOptions, setProductsLoadFailed, setProductsLoading);

        axiosInstance.get(Api.subscription.plan).then(({ data }) => {
            const plans = data?.content || data || [];
            setPlanOptions(plans.filter(plan => plan.status === "ACTIVE")
                .map(plan => ({ value: plan.id, label: plan.name })));
        }).catch(() => {
            setPlanOptions([]);
            setPlansLoadFailed(true);
        }).finally(() => setPlansLoading(false));

        axiosInstance.get(`${Api.main.branch}/user`).then(({ data }) => {
            setBranchOptions((data || [])
                .filter(branch => branch.status === 'ACTIVE')
                .map(branch => ({ value: branch.id, label: branch.name })));
        }).catch(() => {
            setBranchOptions([]);
            setBranchesLoadFailed(true);
        }).finally(() => setBranchesLoading(false));

        axiosInstance.post(`${Api.main.employee}/findByFilters`, {
            filters: [
                { field: 'status', operation: SearchOperation.EQUAL, value: 'ACTIVE' },
                { field: 'providesService', operation: SearchOperation.EQUAL, value: true }
            ]
        }).then(({ data }) => {
            setEmployeeOptions((data || []).map(employee => ({
                value: employee.id,
                label: employee.fullName,
                branchIds: (employee.branches || []).map(branch => branch.id)
            })));
        }).catch(() => {
            setEmployeeOptions([]);
            setEmployeesLoadFailed(true);
        }).finally(() => setEmployeesLoading(false));
    }, []);

    const selectedBranchIds = formik.values.allowedBranchIds || [];
    const scopedEmployeeOptions = employeeOptions.filter(option =>
        selectedBranchIds.length === 0 || option.branchIds.some(branchId => selectedBranchIds.includes(branchId))
    );

    useEffect(() => {
        if (employeesLoading || employeesLoadFailed || selectedBranchIds.length === 0) {
            return;
        }
        const eligibleEmployeeIds = new Set(scopedEmployeeOptions.map(option => option.value));
        const selectedEmployeeIds = formik.values.allowedEmployeeIds || [];
        const nextEmployeeIds = selectedEmployeeIds.filter(id => eligibleEmployeeIds.has(id));
        if (nextEmployeeIds.length !== selectedEmployeeIds.length) {
            formik.setFieldValue('allowedEmployeeIds', nextEmployeeIds);
        }
        // Remove da autorização quem não atende em nenhuma filial permitida pelo agente.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBranchIds.join(','), employeeOptions, employeesLoading, employeesLoadFailed]);

    useEffect(() => {
        if (entityData) {
            formik.setFieldValue('name', entityData.name);
            formik.setFieldValue('description', entityData.description);
            formik.setFieldValue('systemPrompt', entityData.systemPrompt || '');
            formik.setFieldValue('communicationStyle', entityData.communicationStyle || 'FRIENDLY');
            formik.setFieldValue('appointmentBookingEnabled', Boolean(entityData.appointmentBookingEnabled));
            formik.setFieldValue('productCatalogEnabled', entityData.productCatalogEnabled !== false);
            formik.setFieldValue('subscriptionPlansEnabled', Boolean(entityData.subscriptionPlansEnabled));
            formik.setFieldValue('branchConsultationEnabled', entityData.branchConsultationEnabled !== false);
            formik.setFieldValue('employeeConsultationEnabled', entityData.employeeConsultationEnabled !== false);
            formik.setFieldValue('appointmentBookingEnabled', Boolean(entityData.appointmentBookingEnabled));
            formik.setFieldValue('billingRemindersEnabled', Boolean(entityData.billingRemindersEnabled));
            formik.setFieldValue('billingReminderIntervalDays', entityData.billingReminderIntervalDays || 1);
            formik.setFieldValue('billingReminderStartTime', entityData.billingReminderStartTime || '09:00');
            formik.setFieldValue('billingReminderEndTime', entityData.billingReminderEndTime || '20:00');
            formik.setFieldValue('billingReminderMaxAttempts', entityData.billingReminderMaxAttempts || 1);
            formik.setFieldValue('billingReminderOptInConfirmed', Boolean(entityData.billingReminderOptInConfirmed));
            formik.setFieldValue('birthdayMessagesEnabled', Boolean(entityData.birthdayMessagesEnabled));
            formik.setFieldValue('birthdayGiftEnabled', Boolean(entityData.birthdayGiftEnabled));
            formik.setFieldValue('birthdayGiftDescription', entityData.birthdayGiftDescription || '');
            formik.setFieldValue('birthdayOptInConfirmed', Boolean(entityData.birthdayOptInConfirmed));
            formik.setFieldValue('appointmentRemindersEnabled', Boolean(entityData.appointmentRemindersEnabled));
            formik.setFieldValue('appointmentSoonReminderEnabled', Boolean(entityData.appointmentSoonReminderEnabled));
            formik.setFieldValue('appointmentCreatedConfirmationEnabled', Boolean(entityData.appointmentCreatedConfirmationEnabled));
            formik.setFieldValue('serviceFeedbackEnabled', Boolean(entityData.serviceFeedbackEnabled));
            formik.setFieldValue('appointmentReminderLeadHours', entityData.appointmentReminderLeadHours || 24);
            formik.setFieldValue('appointmentReminderStartTime', (entityData.appointmentReminderStartTime || '08:00').slice(0, 5));
            formik.setFieldValue('appointmentReminderEndTime', (entityData.appointmentReminderEndTime || '20:00').slice(0, 5));
            formik.setFieldValue('inactivityFollowUpEnabled', Boolean(entityData.inactivityFollowUpEnabled));
            formik.setFieldValue('inactivityThresholdMinutes', entityData.inactivityThresholdMinutes ?? 30);
            formik.setFieldValue('inactivityFollowUpIntervalMinutes', entityData.inactivityFollowUpIntervalMinutes ?? 30);
            formik.setFieldValue('maxInactivityFollowUpAttempts', entityData.maxInactivityFollowUpAttempts ?? 2);
            formik.setFieldValue('inactivityFollowUpEscalateToHumanEnabled', Boolean(entityData.inactivityFollowUpEscalateToHumanEnabled));
            formik.setFieldValue('inactivityFollowUpStartTime', entityData.inactivityFollowUpStartTime || '');
            formik.setFieldValue('inactivityFollowUpEndTime', entityData.inactivityFollowUpEndTime || '');
            formik.setFieldValue('allowedServiceIds', entityData.allowedServiceIds || []);
            formik.setFieldValue('allowedBranchIds', entityData.allowedBranchIds || []);
            formik.setFieldValue('allowedEmployeeIds', entityData.allowedEmployeeIds || []);
            formik.setFieldValue('allowedProductIds', entityData.allowedProductIds || []);
            formik.setFieldValue('allowedPlanIds', entityData.allowedPlanIds || []);
            setStatus(convertStatusToBoolean(entityData.status));
        }

        // eslint-disable-next-line
    }, [entityData]);

    useEffect(() => {
        if (entityData && !servicesLoading && !servicesLoadFailed) {
            const activeIds = new Set(serviceOptions.map(option => option.value));
            formik.setFieldValue('allowedServiceIds', (entityData.allowedServiceIds || []).filter(id => activeIds.has(id)));
        }
        // A seleção antiga pode conter serviços que foram inativados e já não aparecem no catálogo.
        // eslint-disable-next-line
    }, [entityData, serviceOptions, servicesLoading, servicesLoadFailed]);

    useEffect(() => {
        if (entityData && !productsLoading && !productsLoadFailed) {
            const activeIds = new Set(productOptions.map(option => option.value));
            formik.setFieldValue('allowedProductIds', (entityData.allowedProductIds || []).filter(id => activeIds.has(id)));
        }
        // Remove seleções que deixaram de existir ou foram inativadas no catálogo.
        // eslint-disable-next-line
    }, [entityData, productOptions, productsLoading, productsLoadFailed]);

    useEffect(() => {
        if (entityData && !plansLoading && !plansLoadFailed) {
            const activeIds = new Set(planOptions.map(option => option.value));
            formik.setFieldValue('allowedPlanIds', (entityData.allowedPlanIds || []).filter(id => activeIds.has(id)));
        }
        // Remove seleções que deixaram de existir ou foram inativadas na assinatura.
        // eslint-disable-next-line
    }, [entityData, planOptions, plansLoading, plansLoadFailed]);

    return (
        <div className="whatsapp-bot-form form-layout form-layout--spaced">
            <Alert variant="info" className="whatsapp-bot-form__info">
                <div className="d-flex align-items-start">
                    <i className="fas fa-info-circle me-3 mt-1" style={{ fontSize: '1.2rem' }}></i>
                    <div>
                        <h6 className="alert-heading mb-2">{t('whatsapp_bot_info_title')}</h6>
                        <p className="mb-0">
                            {t('whatsapp_bot_info_description')}
                        </p>
                    </div>
                </div>
            </Alert>

            <FormSection
                title={t('general_information')}
                description={t('bot_basic_info_description')}
                icon="fa-solid fa-robot"
            >
                <Row className="g-3">
                    <Col md={12}>
                        <FormGroup>
                            <FormLabel>
                                {t('chatbot_assistant_name')} <AsteriskRequired />
                                <HintTooltip text={t('bot_name_help')} />
                            </FormLabel>
                            <InputText
                                name="name"
                                placeholder={t('bot_name_placeholder')}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                                error={formik.errors.name}
                                maxlength="100"
                            />
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup>
                            <FormLabel>
                                {t('description')}
                                <HintTooltip text={t('bot_description_help')} />
                            </FormLabel>
                            <InputText
                                name="description"
                                placeholder={t('bot_description_placeholder')}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.description}
                                maxLength={255}
                            />
                        </FormGroup>
                    </Col>
                </Row>
            </FormSection>

            <FormSection
                title={t('chatbot_profile_title')}
                description={t('chatbot_profile_description')}
                icon="fa-solid fa-user-astronaut"
            >
                <Row className="g-3">
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>
                                {t('chatbot_communication_style')}
                                <HintTooltip text={t('chatbot_communication_style_help')} />
                            </FormLabel>
                            <CustomSelect
                                options={communicationStyleOptions}
                                value={communicationStyleOptions.find(option => option.value === formik.values.communicationStyle)}
                                onChange={(option) => formik.setFieldValue('communicationStyle', option?.value || 'FRIENDLY')}
                                isClearable={false}
                                placeholder={t('select')}
                            />
                        </FormGroup>
                    </Col>
                </Row>
            </FormSection>

            <FormSection
                title={t('chatbot_capabilities_title')}
                description={t('chatbot_capabilities_description')}
                icon="fa-solid fa-wand-magic-sparkles"
            >
                <section className="whatsapp-bot-form__billing-card mt-3" aria-labelledby="appointmentBookingTitle">
                    <div className="whatsapp-bot-form__billing-header">
                        <div className="whatsapp-bot-form__billing-identity">
                            <span className="whatsapp-bot-form__billing-icon" aria-hidden="true">
                                <i className="fas fa-calendar-check" />
                            </span>
                            <div>
                                <h6 id="appointmentBookingTitle" className="whatsapp-bot-form__billing-title">
                                    {t('chatbot_booking_enable')}
                                    <HintTooltip text={t('chatbot_booking_enable_help')} />
                                </h6>
                                <p className="whatsapp-bot-form__billing-description">
                                    {t('chatbot_booking_summary')}
                                </p>
                            </div>
                        </div>
                        <div className="form-check form-switch whatsapp-bot-form__billing-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="appointmentBookingEnabled"
                                checked={Boolean(formik.values.appointmentBookingEnabled)}
                                onChange={(event) => formik.setFieldValue('appointmentBookingEnabled', event.target.checked)}
                            />
                            <label className="visually-hidden" htmlFor="appointmentBookingEnabled">
                                {t('chatbot_booking_enable')}
                            </label>
                        </div>
                    </div>
                </section>

                <section className="whatsapp-bot-form__billing-card mt-3" aria-labelledby="billingRemindersTitle">
                    <div className="whatsapp-bot-form__billing-header">
                        <div className="whatsapp-bot-form__billing-identity">
                            <span className="whatsapp-bot-form__billing-icon" aria-hidden="true">
                                <i className="fas fa-receipt" />
                            </span>
                            <div>
                                <h6 id="billingRemindersTitle" className="whatsapp-bot-form__billing-title">
                                    {t('chatbot_billing_reminders_enable')}
                                    <HintTooltip text={t('chatbot_billing_reminders_help')} />
                                </h6>
                                <p className="whatsapp-bot-form__billing-description">
                                    {t('chatbot_billing_reminders_summary')}
                                </p>
                            </div>
                        </div>
                        <div className="form-check form-switch whatsapp-bot-form__billing-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="billingRemindersEnabled"
                                checked={Boolean(formik.values.billingRemindersEnabled)}
                                onChange={(event) => formik.setFieldValue('billingRemindersEnabled', event.target.checked)}
                            />
                            <label className="visually-hidden" htmlFor="billingRemindersEnabled">
                                {t('chatbot_billing_reminders_enable')}
                            </label>
                        </div>
                    </div>

                    {formik.values.billingRemindersEnabled && (
                        <div className="whatsapp-bot-form__billing-settings">
                            <div className="whatsapp-bot-form__billing-notice">
                                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                                <span>{t('chatbot_billing_reminders_policy_notice')}</span>
                            </div>
                            <div className="whatsapp-bot-form__billing-fields">
                                <div className="whatsapp-bot-form__billing-group">
                                    <h6>{t('chatbot_billing_frequency_heading')}</h6>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <FormLabel>{t('chatbot_billing_reminder_interval')}</FormLabel>
                                                <input className="form-control" type="number" min="1" max="365" name="billingReminderIntervalDays" value={formik.values.billingReminderIntervalDays} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                                {formik.errors.billingReminderIntervalDays && <small className="text-danger">{formik.errors.billingReminderIntervalDays}</small>}
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <FormLabel>{t('chatbot_billing_reminder_attempts')}</FormLabel>
                                                <input className="form-control" type="number" min="1" max="10" name="billingReminderMaxAttempts" value={formik.values.billingReminderMaxAttempts} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                                {formik.errors.billingReminderMaxAttempts && <small className="text-danger">{formik.errors.billingReminderMaxAttempts}</small>}
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>
                                <div className="whatsapp-bot-form__billing-group">
                                    <h6>{t('chatbot_billing_send_window_heading')}</h6>
                                    <p className="text-muted mb-3">{t('chatbot_billing_send_window_help')}</p>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <FormLabel>{t('chatbot_billing_send_window_start')}</FormLabel>
                                                <input className="form-control" type="time" name="billingReminderStartTime" value={formik.values.billingReminderStartTime} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                                {formik.errors.billingReminderStartTime && <small className="text-danger">{formik.errors.billingReminderStartTime}</small>}
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <FormLabel>{t('chatbot_billing_send_window_end')}</FormLabel>
                                                <input className="form-control" type="time" name="billingReminderEndTime" value={formik.values.billingReminderEndTime} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                                {formik.errors.billingReminderEndTime && <small className="text-danger">{formik.errors.billingReminderEndTime}</small>}
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>

                            </div>
                            <div className={`whatsapp-bot-form__birthday-consent${formik.errors.billingReminderOptInConfirmed ? ' has-error' : ''}`}>
                                <label htmlFor="billingReminderOptInConfirmed" className="whatsapp-bot-form__birthday-consent-label">
                                    <input
                                        id="billingReminderOptInConfirmed"
                                        className="form-check-input"
                                        type="checkbox"
                                        name="billingReminderOptInConfirmed"
                                        checked={Boolean(formik.values.billingReminderOptInConfirmed)}
                                        onChange={formik.handleChange}
                                    />
                                    <span>
                                        <strong>{t('chatbot_billing_opt_in_confirmation')}</strong>
                                        <small>{t('chatbot_billing_opt_in_help')}</small>
                                    </span>
                                </label>
                                {formik.errors.billingReminderOptInConfirmed && (
                                    <div className="whatsapp-bot-form__birthday-consent-error" role="alert">
                                        <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                                        <span>{formik.errors.billingReminderOptInConfirmed}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </section>

                <section className="whatsapp-bot-form__billing-card mt-3" aria-labelledby="birthdayMessagesTitle">
                    <div className="whatsapp-bot-form__billing-header">
                        <div className="whatsapp-bot-form__billing-identity">
                            <span className="whatsapp-bot-form__billing-icon" aria-hidden="true">
                                <i className="fas fa-cake-candles" />
                            </span>
                            <div>
                                <h6 id="birthdayMessagesTitle" className="whatsapp-bot-form__billing-title">
                                    {t('chatbot_birthday_messages_enable')}
                                    <HintTooltip text={t('chatbot_birthday_messages_help')} />
                                </h6>
                                <p className="whatsapp-bot-form__billing-description">
                                    {t('chatbot_birthday_messages_summary')}
                                </p>
                            </div>
                        </div>
                        <div className="form-check form-switch whatsapp-bot-form__billing-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="birthdayMessagesEnabled"
                                checked={Boolean(formik.values.birthdayMessagesEnabled)}
                                onChange={(event) => formik.setFieldValue('birthdayMessagesEnabled', event.target.checked)}
                            />
                            <label className="visually-hidden" htmlFor="birthdayMessagesEnabled">
                                {t('chatbot_birthday_messages_enable')}
                            </label>
                        </div>
                    </div>

                    {formik.values.birthdayMessagesEnabled && (
                        <div className="whatsapp-bot-form__billing-settings">
                            <div className="whatsapp-bot-form__billing-notice">
                                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                                <span>{t('chatbot_birthday_messages_policy_notice')}</span>
                            </div>
                            <div className="form-check form-switch mb-3">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="birthdayGiftEnabled"
                                    checked={Boolean(formik.values.birthdayGiftEnabled)}
                                    onChange={(event) => formik.setFieldValue('birthdayGiftEnabled', event.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="birthdayGiftEnabled">
                                    {t('chatbot_birthday_gift_enable')}
                                </label>
                            </div>
                            {formik.values.birthdayGiftEnabled && (
                                <FormGroup className="mb-3">
                                    <FormLabel>{t('chatbot_birthday_gift_description')}</FormLabel>
                                    <TextArea
                                        name="birthdayGiftDescription"
                                        value={formik.values.birthdayGiftDescription || ''}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={t('chatbot_birthday_gift_placeholder')}
                                        maxLength={200}
                                        rows={3}
                                    />
                                    {formik.errors.birthdayGiftDescription && <small className="text-danger">{formik.errors.birthdayGiftDescription}</small>}
                                </FormGroup>
                            )}
                            <div className={`whatsapp-bot-form__birthday-consent${formik.errors.birthdayOptInConfirmed ? ' has-error' : ''}`}>
                                <label htmlFor="birthdayOptInConfirmed" className="whatsapp-bot-form__birthday-consent-label">
                                    <input
                                        id="birthdayOptInConfirmed"
                                        className="form-check-input"
                                        type="checkbox"
                                        name="birthdayOptInConfirmed"
                                        checked={Boolean(formik.values.birthdayOptInConfirmed)}
                                        onChange={formik.handleChange}
                                    />
                                    <span>
                                        <strong>{t('chatbot_birthday_opt_in_confirmation')}</strong>
                                        <small>{t('chatbot_birthday_opt_in_help')}</small>
                                    </span>
                                </label>
                                {formik.errors.birthdayOptInConfirmed && (
                                    <div className="whatsapp-bot-form__birthday-consent-error" role="alert">
                                        <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                                        <span>{formik.errors.birthdayOptInConfirmed}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </section>
                <section className="whatsapp-bot-form__billing-card mt-3" aria-labelledby="appointmentRemindersTitle">
                    <div className="whatsapp-bot-form__billing-header">
                        <div className="whatsapp-bot-form__billing-identity">
                            <span className="whatsapp-bot-form__billing-icon" aria-hidden="true">
                                <i className="fas fa-calendar-check" />
                            </span>
                            <div>
                                <h6 id="appointmentRemindersTitle" className="whatsapp-bot-form__billing-title">
                                    {t('chatbot_appointment_reminders_enable')}
                                    <HintTooltip text={t('chatbot_appointment_reminders_help')} />
                                </h6>
                                <p className="whatsapp-bot-form__billing-description">
                                    {t('chatbot_appointment_reminders_summary')}
                                </p>
                            </div>
                        </div>
                        <div className="form-check form-switch whatsapp-bot-form__billing-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="appointmentRemindersEnabled"
                                checked={Boolean(formik.values.appointmentRemindersEnabled)}
                                onChange={(event) => formik.setFieldValue('appointmentRemindersEnabled', event.target.checked)}
                            />
                            <label className="visually-hidden" htmlFor="appointmentRemindersEnabled">
                                {t('chatbot_appointment_reminders_enable')}
                            </label>
                        </div>
                    </div>

                    {formik.values.appointmentRemindersEnabled && (
                        <div className="whatsapp-bot-form__billing-settings">
                            <div className="whatsapp-bot-form__billing-notice">
                                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                                <span>{t('chatbot_appointment_reminders_notice')}</span>
                            </div>
                            <Row className="g-3">
                                <Col md={4}>
                                    <FormGroup>
                                        <FormLabel>{t('chatbot_appointment_reminders_lead_hours')}</FormLabel>
                                        <input className="form-control" type="number" min="1" max="72" name="appointmentReminderLeadHours" value={formik.values.appointmentReminderLeadHours} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                        {formik.errors.appointmentReminderLeadHours && <small className="text-danger">{formik.errors.appointmentReminderLeadHours}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <FormLabel>{t('chatbot_billing_send_window_start')}</FormLabel>
                                        <input className="form-control" type="time" name="appointmentReminderStartTime" value={formik.values.appointmentReminderStartTime} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                        {formik.errors.appointmentReminderStartTime && <small className="text-danger">{formik.errors.appointmentReminderStartTime}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <FormLabel>{t('chatbot_billing_send_window_end')}</FormLabel>
                                        <input className="form-control" type="time" name="appointmentReminderEndTime" value={formik.values.appointmentReminderEndTime} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                        {formik.errors.appointmentReminderEndTime && <small className="text-danger">{formik.errors.appointmentReminderEndTime}</small>}
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    )}
                </section>
                <section className="whatsapp-bot-form__billing-card mt-3" aria-labelledby="appointmentSoonReminderTitle">
                    <div className="whatsapp-bot-form__billing-header">
                        <div className="whatsapp-bot-form__billing-identity">
                            <span className="whatsapp-bot-form__billing-icon" aria-hidden="true">
                                <i className="fas fa-bell" />
                            </span>
                            <div>
                                <h6 id="appointmentSoonReminderTitle" className="whatsapp-bot-form__billing-title">
                                    {t('chatbot_appointment_soon_reminder_enable')}
                                    <HintTooltip text={t('chatbot_appointment_soon_reminder_help')} />
                                </h6>
                                <p className="whatsapp-bot-form__billing-description">
                                    {t('chatbot_appointment_soon_reminder_summary')}
                                </p>
                            </div>
                        </div>
                        <div className="form-check form-switch whatsapp-bot-form__billing-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="appointmentSoonReminderEnabled"
                                checked={Boolean(formik.values.appointmentSoonReminderEnabled)}
                                onChange={(event) => formik.setFieldValue('appointmentSoonReminderEnabled', event.target.checked)}
                            />
                            <label className="visually-hidden" htmlFor="appointmentSoonReminderEnabled">
                                {t('chatbot_appointment_soon_reminder_enable')}
                            </label>
                        </div>
                    </div>
                    {formik.values.appointmentSoonReminderEnabled && (
                        <div className="whatsapp-bot-form__billing-settings">
                            <div className="whatsapp-bot-form__billing-notice">
                                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                                <span>{t('chatbot_appointment_soon_reminder_notice')}</span>
                            </div>
                        </div>
                    )}
                </section>
                <section className="whatsapp-bot-form__billing-card mt-3" aria-labelledby="appointmentCreatedConfirmationTitle">
                    <div className="whatsapp-bot-form__billing-header">
                        <div className="whatsapp-bot-form__billing-identity">
                            <span className="whatsapp-bot-form__billing-icon" aria-hidden="true">
                                <i className="fas fa-calendar-check" />
                            </span>
                            <div>
                                <h6 id="appointmentCreatedConfirmationTitle" className="whatsapp-bot-form__billing-title">
                                    {t('chatbot_appointment_created_confirmation_enable')}
                                    <HintTooltip text={t('chatbot_appointment_created_confirmation_help')} />
                                </h6>
                                <p className="whatsapp-bot-form__billing-description">
                                    {t('chatbot_appointment_created_confirmation_summary')}
                                </p>
                            </div>
                        </div>
                        <div className="form-check form-switch whatsapp-bot-form__billing-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="appointmentCreatedConfirmationEnabled"
                                checked={Boolean(formik.values.appointmentCreatedConfirmationEnabled)}
                                onChange={(event) => formik.setFieldValue('appointmentCreatedConfirmationEnabled', event.target.checked)}
                            />
                            <label className="visually-hidden" htmlFor="appointmentCreatedConfirmationEnabled">
                                {t('chatbot_appointment_created_confirmation_enable')}
                            </label>
                        </div>
                    </div>
                    {formik.values.appointmentCreatedConfirmationEnabled && (
                        <div className="whatsapp-bot-form__billing-settings">
                            <div className="whatsapp-bot-form__billing-notice">
                                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                                <span>{t('chatbot_appointment_created_confirmation_notice')}</span>
                            </div>
                        </div>
                    )}
                </section>
                <section className="whatsapp-bot-form__billing-card mt-3" aria-labelledby="serviceFeedbackTitle">
                    <div className="whatsapp-bot-form__billing-header">
                        <div className="whatsapp-bot-form__billing-identity">
                            <span className="whatsapp-bot-form__billing-icon" aria-hidden="true">
                                <i className="fas fa-star" />
                            </span>
                            <div>
                                <h6 id="serviceFeedbackTitle" className="whatsapp-bot-form__billing-title">
                                    {t('chatbot_service_feedback_enable')}
                                    <HintTooltip text={t('chatbot_service_feedback_help')} />
                                </h6>
                                <p className="whatsapp-bot-form__billing-description">
                                    {t('chatbot_service_feedback_summary')}
                                </p>
                            </div>
                        </div>
                        <div className="form-check form-switch whatsapp-bot-form__billing-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="serviceFeedbackEnabled"
                                checked={Boolean(formik.values.serviceFeedbackEnabled)}
                                onChange={(event) => formik.setFieldValue('serviceFeedbackEnabled', event.target.checked)}
                            />
                            <label className="visually-hidden" htmlFor="serviceFeedbackEnabled">
                                {t('chatbot_service_feedback_enable')}
                            </label>
                        </div>
                    </div>
                    {formik.values.serviceFeedbackEnabled && (
                        <div className="whatsapp-bot-form__billing-settings">
                            <div className="whatsapp-bot-form__billing-notice">
                                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                                <span>{t('chatbot_service_feedback_notice')}</span>
                            </div>
                        </div>
                    )}
                </section>
                <section className="whatsapp-bot-form__billing-card mt-3" aria-labelledby="inactivityFollowUpTitle">
                    <div className="whatsapp-bot-form__billing-header">
                        <div className="whatsapp-bot-form__billing-identity">
                            <span className="whatsapp-bot-form__billing-icon" aria-hidden="true">
                                <i className="fas fa-user-clock" />
                            </span>
                            <div>
                                <h6 id="inactivityFollowUpTitle" className="whatsapp-bot-form__billing-title">
                                    {t('chatbot_reengagement_title')}
                                </h6>
                                <p className="whatsapp-bot-form__billing-description">{t('chatbot_reengagement_description')}</p>
                            </div>
                        </div>
                        <div className="form-check form-switch whatsapp-bot-form__billing-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="inactivityFollowUpEnabled"
                                checked={Boolean(formik.values.inactivityFollowUpEnabled)}
                                onChange={(event) => formik.setFieldValue('inactivityFollowUpEnabled', event.target.checked)}
                            />
                            <label className="visually-hidden" htmlFor="inactivityFollowUpEnabled">{t('chatbot_reengagement_enable')}</label>
                        </div>
                    </div>
                    {formik.values.inactivityFollowUpEnabled && (
                        <div className="whatsapp-bot-form__billing-settings">
                            <div className="whatsapp-bot-form__billing-notice">
                                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                                <span>{t('chatbot_reengagement_summary', {
                                    threshold: formik.values.inactivityThresholdMinutes || 30,
                                    interval: formik.values.inactivityFollowUpIntervalMinutes || 30,
                                    attempts: formik.values.maxInactivityFollowUpAttempts ?? 2,
                                    unit: t('minutes')
                                })}</span>
                            </div>
                            <Row className="g-3">
                                <Col md={4}>
                                    <FormGroup>
                                        <FormLabel>{t('chatbot_inactivity_threshold_minutes')}</FormLabel>
                                        <input className="form-control" type="number" min="1" name="inactivityThresholdMinutes"
                                            value={formik.values.inactivityThresholdMinutes} onChange={formik.handleChange} />
                                        {formik.errors.inactivityThresholdMinutes && <small className="text-danger">{formik.errors.inactivityThresholdMinutes}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <FormLabel>{t('chatbot_follow_up_interval_minutes')}</FormLabel>
                                        <input className="form-control" type="number" min="1" name="inactivityFollowUpIntervalMinutes"
                                            value={formik.values.inactivityFollowUpIntervalMinutes} onChange={formik.handleChange} />
                                        {formik.errors.inactivityFollowUpIntervalMinutes && <small className="text-danger">{formik.errors.inactivityFollowUpIntervalMinutes}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <FormLabel>{t('chatbot_max_follow_up_attempts')}</FormLabel>
                                        <input className="form-control" type="number" min="0" name="maxInactivityFollowUpAttempts"
                                            value={formik.values.maxInactivityFollowUpAttempts} onChange={formik.handleChange} />
                                        {formik.errors.maxInactivityFollowUpAttempts && <small className="text-danger">{formik.errors.maxInactivityFollowUpAttempts}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <FormLabel>{t('chatbot_reengagement_start_time')}</FormLabel>
                                        <input className="form-control" type="time" name="inactivityFollowUpStartTime"
                                            value={formik.values.inactivityFollowUpStartTime || ''} onChange={formik.handleChange} />
                                        {formik.errors.inactivityFollowUpStartTime && <small className="text-danger">{formik.errors.inactivityFollowUpStartTime}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <FormLabel>{t('chatbot_reengagement_end_time')}</FormLabel>
                                        <input className="form-control" type="time" name="inactivityFollowUpEndTime"
                                            value={formik.values.inactivityFollowUpEndTime || ''} onChange={formik.handleChange} />
                                        {formik.errors.inactivityFollowUpEndTime && <small className="text-danger">{formik.errors.inactivityFollowUpEndTime}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md={12}>
                                    <div className="whatsapp-bot-form__capability">
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="inactivityFollowUpEscalateToHumanEnabled"
                                                checked={Boolean(formik.values.inactivityFollowUpEscalateToHumanEnabled)}
                                                onChange={(event) => formik.setFieldValue('inactivityFollowUpEscalateToHumanEnabled', event.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="inactivityFollowUpEscalateToHumanEnabled">
                                                {t('chatbot_reengagement_human_queue')}
                                                <HintTooltip text={t('chatbot_reengagement_human_queue_help')} />
                                            </label>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </section>
            </FormSection>

            <FormSection
                title={t('chatbot_data_access_title')}
                description={t('chatbot_data_access_description')}
                icon="fa-solid fa-database"
            >
                <Row className="g-3">
                    <Col md={12}>
                        <div className="whatsapp-bot-form__capability">
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="branchConsultationEnabled"
                                    checked={Boolean(formik.values.branchConsultationEnabled)}
                                    onChange={(event) => formik.setFieldValue('branchConsultationEnabled', event.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="branchConsultationEnabled">
                                    {t('chatbot_branch_consultation_access')}
                                    <HintTooltip text={t('chatbot_branch_consultation_access_help')} />
                                </label>
                            </div>
                            <div className="whatsapp-bot-form__service-scope-copy">
                                <strong>{t('chatbot_allowed_branches')}</strong>
                                <small>{t('chatbot_allowed_branches_help')}</small>
                            </div>
                            <MultiSelect
                                inputId="allowedBranches"
                                aria-label={t('chatbot_allowed_branches')}
                                options={branchOptions}
                                value={branchOptions.filter(option => (formik.values.allowedBranchIds || []).includes(option.value))}
                                onChange={options => formik.setFieldValue('allowedBranchIds', (options || []).map(option => option.value))}
                                placeholder={t('chatbot_allowed_branches_placeholder')}
                                isDisabled={!formik.values.branchConsultationEnabled || branchesLoading || branchesLoadFailed}
                                isLoading={branchesLoading}
                            />
                            {(branchesLoading || branchesLoadFailed || branchOptions.length === 0) && (
                                <small className="text-muted d-block mt-2">
                                    {branchesLoading ? t('loading') : branchesLoadFailed ? t('chatbot_allowed_branches_load_error') : t('chatbot_allowed_branches_empty')}
                                </small>
                            )}
                        </div>
                    </Col>
                    <Col md={12}>
                        <div className="whatsapp-bot-form__capability">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="employeeConsultationEnabled"
                                        checked={Boolean(formik.values.employeeConsultationEnabled)}
                                        onChange={(event) => formik.setFieldValue('employeeConsultationEnabled', event.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="employeeConsultationEnabled">
                                        {t('chatbot_employee_consultation_access')}
                                        <HintTooltip text={t('chatbot_employee_consultation_access_help')} />
                                    </label>
                                </div>
                                <div className="whatsapp-bot-form__service-scope-copy">
                                    <strong>{t('chatbot_allowed_employees')}</strong>
                                    <small>{t('chatbot_allowed_employees_help')}</small>
                                </div>
                                <MultiSelect
                                    inputId="allowedEmployees"
                                    aria-label={t('chatbot_allowed_employees')}
                                    options={scopedEmployeeOptions}
                                    value={scopedEmployeeOptions.filter(option => (formik.values.allowedEmployeeIds || []).includes(option.value))}
                                    onChange={options => formik.setFieldValue('allowedEmployeeIds', (options || []).map(option => option.value))}
                                    placeholder={t('chatbot_allowed_employees_placeholder')}
                                    isDisabled={!formik.values.employeeConsultationEnabled || employeesLoading || employeesLoadFailed}
                                    isLoading={employeesLoading}
                                />
                                {(employeesLoading || employeesLoadFailed || employeeOptions.length === 0) && (
                                    <small className="text-muted d-block mt-2">
                                        {employeesLoading ? t('loading') : employeesLoadFailed ? t('chatbot_allowed_employees_load_error') : t('chatbot_allowed_employees_empty')}
                                    </small>
                                )}
                        </div>
                    </Col>
                    <Col md={12}>
                        <div className="whatsapp-bot-form__capability whatsapp-bot-form__plans">
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="subscriptionPlansEnabled"
                                    checked={Boolean(formik.values.subscriptionPlansEnabled)}
                                    onChange={(event) => formik.setFieldValue('subscriptionPlansEnabled', event.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="subscriptionPlansEnabled">
                                    {t('chatbot_subscription_plans_access')}
                                    <HintTooltip text={t('chatbot_subscription_plans_access_help')} />
                                </label>
                            </div>

                            <div className="whatsapp-bot-form__service-scope">
                                <div className="whatsapp-bot-form__service-scope-copy">
                                    <strong>{t('chatbot_allowed_plans')}</strong>
                                    <small>{t('chatbot_allowed_plans_help')}</small>
                                </div>
                                <MultiSelect
                                    inputId="allowedPlans"
                                    aria-label={t('chatbot_allowed_plans')}
                                    options={planOptions}
                                    value={planOptions.filter(option => (formik.values.allowedPlanIds || []).includes(option.value))}
                                    onChange={options => formik.setFieldValue('allowedPlanIds', (options || []).map(option => option.value))}
                                    placeholder={t('chatbot_allowed_plans_placeholder')}
                                    isDisabled={!formik.values.subscriptionPlansEnabled || plansLoading || plansLoadFailed}
                                    isLoading={plansLoading}
                                />
                                {(plansLoading || plansLoadFailed || planOptions.length === 0) && (
                                    <small className="text-muted d-block mt-2">
                                        {plansLoading ? t('loading') : plansLoadFailed ? t('chatbot_allowed_plans_load_error') : t('chatbot_allowed_plans_empty')}
                                    </small>
                                )}
                            </div>
                        </div>
                    </Col>
                    <Col md={12}>
                        <div className="whatsapp-bot-form__capability whatsapp-bot-form__catalog">
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="productCatalogEnabled"
                                    checked={Boolean(formik.values.productCatalogEnabled)}
                                    onChange={(event) => formik.setFieldValue('productCatalogEnabled', event.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="productCatalogEnabled">
                                    {t('chatbot_product_catalog_access')}
                                    <HintTooltip text={t('chatbot_product_catalog_access_help')} />
                                </label>
                            </div>

                            <div className="whatsapp-bot-form__service-scope">
                                <div className="whatsapp-bot-form__service-scope-copy">
                                    <strong>{t('chatbot_allowed_products')}</strong>
                                    <small>{t('chatbot_allowed_products_help')}</small>
                                </div>
                                <MultiSelect
                                    inputId="allowedProducts"
                                    aria-label={t('chatbot_allowed_products')}
                                    options={productOptions}
                                    value={productOptions.filter(option => (formik.values.allowedProductIds || []).includes(option.value))}
                                    onChange={options => formik.setFieldValue('allowedProductIds', (options || []).map(option => option.value))}
                                    placeholder={t('chatbot_allowed_products_placeholder')}
                                    isDisabled={!formik.values.productCatalogEnabled || productsLoading || productsLoadFailed}
                                    isLoading={productsLoading}
                                />
                                {(productsLoading || productsLoadFailed || productOptions.length === 0) && (
                                    <small className="text-muted d-block mt-2">
                                        {productsLoading ? t('loading') : productsLoadFailed ? t('chatbot_allowed_products_load_error') : t('chatbot_allowed_products_empty')}
                                    </small>
                                )}
                            </div>

                            <div className="whatsapp-bot-form__service-scope">
                                <div className="whatsapp-bot-form__service-scope-copy">
                                    <strong>{t('chatbot_allowed_services')}</strong>
                                    <small>{t('chatbot_allowed_services_help')}</small>
                                </div>
                                <MultiSelect
                                    inputId="allowedServices"
                                    aria-label={t('chatbot_allowed_services')}
                                    options={serviceOptions}
                                    value={serviceOptions.filter(option => (formik.values.allowedServiceIds || []).includes(option.value))}
                                    onChange={options => formik.setFieldValue('allowedServiceIds', (options || []).map(option => option.value))}
                                    placeholder={t('chatbot_allowed_services_placeholder')}
                                    isDisabled={!formik.values.productCatalogEnabled || servicesLoading || servicesLoadFailed}
                                    isLoading={servicesLoading}
                                />
                                {(servicesLoading || servicesLoadFailed || serviceOptions.length === 0) && (
                                    <small className="text-muted d-block mt-2">
                                        {servicesLoading ? t('loading') : servicesLoadFailed ? t('chatbot_allowed_services_load_error') : t('chatbot_allowed_services_empty')}
                                    </small>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            </FormSection>

            <FormSection
                title={t('bot_system_prompt_title')}
                description={t('bot_system_prompt_description')}
                icon="fa-solid fa-brain"
                className="whatsapp-bot-form__prompt"
            >
                <Row>
                    <Col md={12}>
                            <FormGroup>
                                <FormLabel>
                                    {t('bot_system_prompt_title')}
                                    <HintTooltip text={t('bot_system_prompt_help')} />
                                </FormLabel>
                                <TextArea
                                    rows={10}
                                    name="systemPrompt"
                                    placeholder={t('bot_system_prompt_placeholder')}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.systemPrompt || ''}
                                    maxLength={4000}
                                    counter
                                    style={{ minHeight: '200px', fontSize: '0.875rem' }}
                                />
                            </FormGroup>
                    </Col>
                </Row>
            </FormSection>

            <FormSection title={t('status')} icon="fa-solid fa-toggle-on" compact>
                <Row>
                    <Col md={5}>
                    <FormGroup>
                        <StatusCheckBox
                            name="status"
                            defaultChecked={status}
                            checked={status}
                            onClick={() => setStatus(!status)} />
                    </FormGroup>
                    </Col>
                </Row>
            </FormSection>
        </div>
    );
};

export default Form;
